import React, { useEffect, useState } from 'react';
import { adminListConsultants, adminCreateConsultant, adminUpdateConsultant, adminDeleteConsultant } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { Users, Trash2, Plus, Loader2 } from 'lucide-react';

const DAYS = [
  { key: 'mon', label: 'Mon' }, { key: 'tue', label: 'Tue' }, { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' }, { key: 'fri', label: 'Fri' }, { key: 'sat', label: 'Sat' }, { key: 'sun', label: 'Sun' },
];

const blank = () => ({ name: '', email: '', role: '', timezone: 'IST', working_days: ['mon','tue','wed','thu','fri'], day_start: '10:00', day_end: '18:00', active: true });

const ConsultantRow = ({ item, onSave, onDelete, saving }) => {
  const [local, setLocal] = useState(item);

  const toggleDay = (d) => {
    const has = local.working_days.includes(d);
    setLocal({ ...local, working_days: has ? local.working_days.filter((x) => x !== d) : [...local.working_days, d] });
  };

  return (
    <div className="rounded-2xl border bg-white p-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Name</Label><Input value={local.name} onChange={(e) => setLocal({ ...local, name: e.target.value })} className="h-10 mt-1" /></div>
        <div><Label>Email</Label><Input type="email" value={local.email} onChange={(e) => setLocal({ ...local, email: e.target.value })} className="h-10 mt-1" /></div>
        <div><Label>Role</Label><Input value={local.role || ''} onChange={(e) => setLocal({ ...local, role: e.target.value })} className="h-10 mt-1" placeholder="Senior Consultant" /></div>
        <div><Label>Timezone</Label><Input value={local.timezone || ''} onChange={(e) => setLocal({ ...local, timezone: e.target.value })} className="h-10 mt-1" placeholder="IST" /></div>
        <div><Label>Day start (HH:MM)</Label><Input value={local.day_start} onChange={(e) => setLocal({ ...local, day_start: e.target.value })} className="h-10 mt-1" placeholder="10:00" /></div>
        <div><Label>Day end (HH:MM)</Label><Input value={local.day_end} onChange={(e) => setLocal({ ...local, day_end: e.target.value })} className="h-10 mt-1" placeholder="18:00" /></div>
      </div>

      <div className="mt-4">
        <Label>Working days</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {DAYS.map((d) => (
            <button key={d.key} type="button" onClick={() => toggleDay(d.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                local.working_days.includes(d.key) ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}>{d.label}</button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={!!local.active} onCheckedChange={(v) => setLocal({ ...local, active: v })} />
          <span className="text-slate-700">{local.active ? 'Active — accepts bookings' : 'Inactive'}</span>
        </label>
        <div className="flex gap-2">
          {item.id && (
            <Button variant="outline" onClick={() => onDelete(item.id)} className="h-9 text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-1" /> Remove
            </Button>
          )}
          <Button onClick={() => onSave(local)} disabled={saving} className="h-9 bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminTeamTab = () => {
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newForm, setNewForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setList(await adminListConsultants()); }
    catch { toast({ title: 'Could not load team' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveExisting = async (item) => {
    setSaving(true);
    try {
      const { id, ...patch } = item;
      const updated = await adminUpdateConsultant(id, patch);
      setList((cur) => cur.map((c) => (c.id === id ? updated : c)));
      toast({ title: 'Saved' });
    } catch { toast({ title: 'Save failed' }); }
    finally { setSaving(false); }
  };

  const saveNew = async (item) => {
    setSaving(true);
    try {
      const created = await adminCreateConsultant(item);
      setList((cur) => [...cur, created]);
      setNewForm(null);
      toast({ title: 'Consultant added' });
    } catch { toast({ title: 'Create failed' }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await adminDeleteConsultant(id);
      setList((cur) => cur.filter((c) => c.id !== id));
    } catch { toast({ title: 'Delete failed' }); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-slate-900 flex items-center gap-2"><Users className="h-4 w-4" /> Team availability ({list.length})</div>
        {!newForm && (
          <Button onClick={() => setNewForm(blank())} className="h-9 bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="h-4 w-4 mr-1" /> Add consultant
          </Button>
        )}
      </div>

      {newForm && (
        <ConsultantRow item={newForm} onSave={saveNew} onDelete={() => setNewForm(null)} saving={saving} />
      )}

      {loading && <div className="text-slate-500 text-sm">Loading…</div>}
      {!loading && list.length === 0 && !newForm && (
        <div className="rounded-2xl border border-dashed border-slate-200 py-12 text-center text-slate-500 text-sm">
          No consultants yet. When no team members exist, ALL slots are open by default. Add someone to enforce availability.
        </div>
      )}

      {list.map((c) => (
        <ConsultantRow key={c.id} item={c} onSave={saveExisting} onDelete={remove} saving={saving} />
      ))}
    </div>
  );
};

export default AdminTeamTab;
